/**
 * "I checked this against the page."
 *
 * The product's central claim is that every figure can be traced to its source. This is what turns
 * that from a property of the system into a record of what a person actually did — and a
 * disagreement recorded here is worth more than any number of confirmations, because it is the
 * only way the team learns where extraction goes wrong.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, friendlyError } from "../api/client";
import { timeAgo, verificationIndex, verificationKey } from "../lib/model";
import { Button, Chip, ErrorBanner } from "./ui";

export default function VerifyFigure({
  caseId,
  fieldName,
  period,
}: {
  caseId: string;
  fieldName: string;
  period: string | null;
}) {
  const queryClient = useQueryClient();
  const [disagreeing, setDisagreeing] = useState(false);
  const [comment, setComment] = useState("");

  const verifications = useQuery({
    queryKey: ["verifications", caseId],
    queryFn: () => api.listVerifications(caseId),
  });

  const state = verificationIndex(verifications.data ?? []).get(
    verificationKey(fieldName, period),
  );

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["verifications", caseId] });
    await queryClient.invalidateQueries({ queryKey: ["activity", caseId] });
  }

  const mark = useMutation({
    mutationFn: (agrees: boolean) =>
      api.verifyFigure(caseId, {
        field_name: fieldName,
        period,
        agrees,
        comment: agrees ? null : comment.trim() || null,
      }),
    onSuccess: async () => {
      setDisagreeing(false);
      setComment("");
      await refresh();
    },
  });

  const clear = useMutation({
    mutationFn: () => api.unverifyFigure(caseId, fieldName, period),
    onSuccess: refresh,
  });

  if (state?.checked) {
    return (
      <div className="mt-4 rounded-lg border border-line p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Chip
            text={state.agrees ? "Verificada contra la página" : "No coincide con la página"}
            tone={state.agrees ? "good" : "bad"}
          />
          <span className="text-xs text-muted">
            {state.by ?? "—"} · {timeAgo(verifications.data?.find((v) => v.field_name === fieldName)?.created_at)}
          </span>
        </div>
        {state.comment ? <p className="mt-2 text-sm">{state.comment}</p> : null}
        <button
          onClick={() => clear.mutate()}
          className="mt-2 text-xs text-muted underline hover:text-ink"
        >
          Deshacer
        </button>
      </div>
    );
  }

  if (disagreeing) {
    return (
      <div className="mt-4 rounded-lg border border-line p-3">
        <label className="block text-sm font-medium" htmlFor="verify-comment">
          ¿Qué dice la página?
        </label>
        <input
          id="verify-comment"
          autoFocus
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Por ejemplo: la página dice 12.400, no 12.500"
          className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />
        {mark.isError ? (
          <div className="mt-2">
            <ErrorBanner {...friendlyError(mark.error)} />
          </div>
        ) : null}
        <div className="mt-2 flex gap-2">
          <Button variant="primary" disabled={mark.isPending} onClick={() => mark.mutate(false)}>
            Registrar la diferencia
          </Button>
          <Button onClick={() => setDisagreeing(false)}>Cancelar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <Button variant="primary" disabled={mark.isPending} onClick={() => mark.mutate(true)}>
        Coincide con la página
      </Button>
      <Button onClick={() => setDisagreeing(true)}>No coincide</Button>
    </div>
  );
}
